import { Hand, Lock, RotateCcw } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SETTLE_SPRING } from "./SectionHeading";

/**
 * The control-first security beat, on ink: the one serious moment on an
 * otherwise sunny page. Three pillars, all of them true today — boundaries
 * and approvals, not certifications.
 */
export function TrustSection() {
  const { t } = useTranslation("home");

  const pillars = [
    { key: "One", icon: RotateCcw, tone: "bg-home-butter" },
    { key: "Two", icon: Hand, tone: "bg-home-sky" },
    { key: "Three", icon: Lock, tone: "bg-home-blush" },
  ] as const;

  return (
    <section className="bg-home-ink px-5 py-20 md:py-24">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={SETTLE_SPRING}
          className="text-center"
        >
          <p className="mb-3 font-body text-[13px] font-bold tracking-[0.16em] text-home-cream/75 uppercase">
            {t("trust.kicker")}
          </p>
          <h2 className="mx-auto max-w-3xl font-display text-[clamp(30px,4vw,52px)] leading-[1.05] font-semibold tracking-tight text-home-cream">
            {t("trust.title")}
          </h2>
          <p className="mt-7 font-display text-[clamp(20px,2.4vw,28px)] leading-snug font-semibold tracking-tight">
            <span className="box-decoration-clone bg-home-yellow px-2 py-0.5 text-home-ink">
              {t("trust.lede")}
            </span>
          </p>
        </motion.div>

        <div className="mt-14 grid gap-5 md:grid-cols-3 md:gap-6">
          {pillars.map(({ key, icon: Icon, tone }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ ...SETTLE_SPRING, delay: 0.09 * i }}
              className="rounded-[28px] bg-white/[0.07] p-7 ring-1 ring-white/10 md:p-8"
            >
              <div className={`mb-5 flex h-11 w-11 items-center justify-center rounded-full ${tone}`}>
                <Icon className="h-5 w-5 text-home-ink" strokeWidth={2.4} />
              </div>
              <h3 className="font-display text-[20px] leading-tight font-semibold text-home-cream">
                {t(`trust.pillar${key}Title`)}
              </h3>
              <p className="mt-3 font-body text-[15px] leading-relaxed text-home-cream/70">
                {t(`trust.pillar${key}Body`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
